import useSession from '@/hooks/useSession';
import {
  Thread,
  ThreadFilters,
  ThreadItem,
  ThreadProcessingStatus,
  ThreadStatus,
  WSThread,
  WSThreadData,
} from '@/models/thread';
import useWebSocket from 'react-use-websocket';
import {
  deleteFromProcessingQueue,
  queueProcessingAction,
} from '../utils/storage';
import { parseFullMessage } from '@/utils/functions';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Session } from '@/models/session';
import { SendJsonMessage } from 'react-use-websocket/dist/lib/types';
import { fetchAuthSession } from 'aws-amplify/auth';
const socketUrl = `${import.meta.env.VITE_BACKEND_WS_LLM}/${
  import.meta.env.VITE_BACKEND_STAGE
}`;

const useThreadWebSocket = ({
  status,
  setStatus,
  items,
  setItems,
  updateThreadProcessing,
  threadID,
  setThreadID,
  setTitle,
  filters,
}: {
  status: ThreadStatus;
  setStatus: Dispatch<SetStateAction<ThreadStatus>>;
  items: ThreadItem[];
  setItems: Dispatch<SetStateAction<ThreadItem[]>>;
  updateThreadProcessing: (
    newValue: ThreadProcessingStatus | undefined
  ) => void;
  threadID: string | undefined;
  setThreadID: Dispatch<SetStateAction<string | undefined>>;
  setTitle: Dispatch<SetStateAction<string>>;
  filters: ThreadFilters;
}): {
  sendWSMessage: SendJsonMessage;
  setIdToken: Dispatch<SetStateAction<string | undefined>>;
} => {
  const { session, refreshThreads, setSession, pushUserEvent } = useSession();
  const [idToken, setIdToken] = useState<string | undefined>(undefined);

  const refreshToken = useCallback(async () => {
    setIdToken(undefined);
    const session = await fetchAuthSession();
    if (session && session.tokens?.idToken) {
      setIdToken(session?.tokens?.idToken.toString());
    } else {
      console.error('Failed to fetch session');
    }
  }, []);

  const { sendJsonMessage } = useWebSocket(
    idToken ? `${socketUrl}/?IdToken=${idToken}` : null,
    {
      onError: (event) => {
        pushUserEvent('error_websocket_failed');
        console.error('Failed to connect', event);
        if (status === 'FETCHING') {
          setStatus('ERROR');
          refreshThreads();
        }
      },
      onMessage: (msg: { data: string }) => {
        try {
          const { success, data: msgData }: WSThread = JSON.parse(
            msg.data.replace(/NaN/g, 'null')
          );

          if (success === false) {
            setStatus('ERROR');
            if (typeof threadID !== 'undefined') {
              deleteFromProcessingQueue(threadID);
            }
            refreshThreads();
            return;
          }

          if (threadID !== undefined && msgData.thread_id !== threadID) {
            pushUserEvent('error_websocket_interrupted');
            return;
          }

          if (msgData.type === 'streamNewMessage') {
            handleResponseStream({
              msgData,
              status,
              setStatus,
              setItems,
            });
          } else if (msgData.type === 'streamSources') {
            handleResponseSources({
              msgData,
              setItems,
            });
          } else if (msgData.type === 'streamComplete') {
            handleResponseEnd({
              msgData,
              setItems,
              setIdToken,
              setStatus,
              updateThreadProcessing,
              refreshThreads,
            });
          } else if (msgData.type === 'streamError') {
            pushUserEvent('error_websocket_stream');
            setIdToken(undefined);
            setStatus('ERROR');
          } else if (status === 'CITING' && msgData.type === 'streamCitation') {
            handleCitationResult({
              msgData,
              setItems,
              pushUserEvent,
              setStatus,
            });
          } else if (success !== undefined && status === 'FETCHING') {
            if (success === true) {
              handleResponseConfirmation({
                msgData,
                session,
                setSession,
                filters,
                threadID,
                setThreadID,
                setTitle,
                items,
                setItems,
              });
            } else {
              console.error('Failed to fetch', msgData);
              setStatus('ERROR');
            }
          }
        } catch (e) {
          pushUserEvent('error_websocket_internal_error');
          console.error(e);
          if (typeof threadID !== 'undefined') {
            deleteFromProcessingQueue(threadID);
          }
          setStatus('ERROR');
        }
      },
      shouldReconnect: () => false,
    }
  );

  const sendWSMessage = useCallback(
    (jsonMessage: unknown) => {
      refreshToken().then(() => {
        sendJsonMessage(jsonMessage);
      });
    },
    [refreshToken, sendJsonMessage]
  );

  return { sendWSMessage, setIdToken };
};

const handleResponseStream = ({
  status,
  msgData,
  setStatus,
  setItems,
}: {
  status: ThreadStatus;
  msgData: WSThreadData;
  setStatus: Dispatch<SetStateAction<ThreadStatus>>;
  setItems: Dispatch<SetStateAction<ThreadItem[]>>;
}) => {
  if (msgData.additional_data === undefined) {
    throw new Error('No additional data');
  }

  if (status === 'FETCHING') {
    setStatus('TYPING');
  }
  setItems((current: ThreadItem[]) => {
    try {
      const item = current[current.length - 1];
      item.response = parseFullMessage(
        `${
          msgData.additional_data?.buffered_streaming_contents || ''
        }${msgData.message}`
      );
      return [...current.slice(0, -1), item];
    } catch (e) {
      console.error(e);
      return current;
    }
  });
};

const handleResponseConfirmation = ({
  msgData,
  session,
  setSession,
  filters,
  threadID,
  setThreadID,
  setTitle,
  items,
  setItems,
}: {
  msgData: WSThreadData;
  session: Session;
  setSession: Dispatch<SetStateAction<Session>>;
  filters: ThreadFilters;
  threadID: string | undefined;
  setThreadID: Dispatch<SetStateAction<string | undefined>>;
  setTitle: Dispatch<SetStateAction<string>>;
  items: ThreadItem[];
  setItems: Dispatch<SetStateAction<ThreadItem[]>>;
}) => {
  const queryDate = new Date(msgData.queryTS || '');

  const newPromptUsage = session.promptUsage;
  const newThreads = [...session.threads];

  newPromptUsage.remainingThisMonth -= 1;
  newPromptUsage.usedThisMonth += 1;

  if (threadID === undefined) {
    setThreadID(msgData.thread_id);
    setTitle(msgData.title as string);
    const newThread: Thread = {
      id: msgData.thread_id,
      title: msgData.title as string,
      items: [
        {
          id: msgData.message_id,
          disliked: false,
          prompt: items[items.length - 1].prompt,
          response: [],
          enabledCitation: true,
          queryDate,
          deepDive: items[items.length - 1].deepDive,
        },
      ],
      filters,
      processingStatus: ThreadProcessingStatus.LIVE,
    };

    newThreads.unshift(newThread);
  } else {
    const threadIndex = newThreads.findIndex(
      (thread) => thread.id === msgData.thread_id
    );

    if (threadIndex !== -1) {
      newThreads[threadIndex].items.push({
        id: msgData.message_id,
        disliked: false,
        prompt: items[items.length - 1].prompt,
        response: [],
        enabledCitation: true,
        queryDate,
        deepDive: items[items.length - 1].deepDive,
      });
      newThreads[threadIndex].processingStatus = ThreadProcessingStatus.LIVE;
    }
  }
  setSession((prev) => ({
    ...prev,
    promptUsage: newPromptUsage,
    threads: newThreads,
  }));
  setItems((current: ThreadItem[]) => {
    const item = current[current.length - 1];
    item.id = msgData.message_id;
    item.enabledCitation = true;

    return [...current.slice(0, -1), item];
  });
  queueProcessingAction({
    type: 'QUERY',
    threadID: msgData.thread_id,
    item: {
      id: msgData.message_id,
      disliked: false,
      prompt: items[items.length - 1].prompt,
      enabledCitation: true,
      queryDate: new Date(msgData.queryTS || ''),
      deepDive: items[items.length - 1].deepDive,
    },
    ts: Number(msgData.queryTS || ''),
  });
};

const handleResponseEnd = ({
  msgData,
  setItems,
  setIdToken,
  setStatus,
  updateThreadProcessing,
  refreshThreads,
}: {
  msgData: WSThreadData;
  setItems: Dispatch<SetStateAction<ThreadItem[]>>;
  setIdToken: Dispatch<SetStateAction<string | undefined>>;
  setStatus: Dispatch<SetStateAction<ThreadStatus>>;
  updateThreadProcessing: (
    newValue: ThreadProcessingStatus | undefined
  ) => void;
  refreshThreads: () => void;
}) => {
  deleteFromProcessingQueue(msgData.thread_id);
  setItems((current: ThreadItem[]) => {
    const item = current[current.length - 1];

    if (
      item.response.length === 0 &&
      !msgData.additional_data?.buffered_streaming_contents &&
      msgData.message
    ) {
      item.response = parseFullMessage(msgData.message);
    } else {
      item.response = parseFullMessage(
        msgData.additional_data?.buffered_streaming_contents || ''
      );
    }

    // streamComplete repeats the sources; use them if streamSources never arrived
    if (
      !item.sources?.length &&
      (msgData.rag_sources?.length || msgData.quantitative_sources?.length)
    ) {
      item.sources = [
        ...(msgData.rag_sources ?? []),
        ...(msgData.quantitative_sources ?? []),
      ];
    }

    return [...current.slice(0, -1), item];
  });

  refreshThreads();
  setIdToken(undefined);
  setStatus('READY');
  updateThreadProcessing(undefined);
};

const handleCitationResult = ({
  msgData,
  setItems,
  pushUserEvent,
  setStatus,
}: {
  msgData: WSThreadData;
  setItems: Dispatch<SetStateAction<ThreadItem[]>>;
  pushUserEvent: (event: string) => void;
  setStatus: Dispatch<SetStateAction<ThreadStatus>>;
}) => {
  const { citation, message_id } = msgData;
  setItems((current) => {
    const itemIndex = current.findIndex((b) => b.id === message_id);

    if (citation === undefined || itemIndex === -1) {
      pushUserEvent('error_citation_not_found');
      return current;
    }
    current[itemIndex] = {
      ...current[itemIndex],
      response: parseFullMessage(citation),
      enabledCitation: false,
    };

    return current;
  });
  setStatus('READY');
};

const handleResponseSources = ({
  msgData,
  setItems,
}: {
  msgData: WSThreadData;
  setItems: Dispatch<SetStateAction<ThreadItem[]>>;
}) => {
  setItems((current: ThreadItem[]) => {
    const item = current[current.length - 1];
    item.sources = [
      ...(msgData.rag_sources ?? []),
      ...(msgData.quantitative_sources ?? []),
    ];

    return [...current.slice(0, -1), item];
  });
};

export default useThreadWebSocket;
